// src/components/common/Poster.js
// Filepath: // src/components/common/Poster.js
// White ring above overlays. Ring visibility is driven by .focused or :focus.
import React from "react";
import { Box, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";

export default function Poster({
  item,
  rowIndex = 0,
  index = 0,
  onEnter,
  onFocus,
  autoFocus,
  width = 372,
  height = 210,
  radius = 12,
  overlayTitle = true,
  overlayHeightPct = 0.35,
  overlayTitleSize = 18,
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (autoFocus && ref.current) {
      if (!ref.current.hasAttribute("tabindex")) ref.current.setAttribute("tabindex", "-1");
      ref.current.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const handleClick = () => onEnter?.(item);
  const handleFocus = () => onFocus?.(item);

  const bg =
    item?.thumbnail ||
    item?.thumbnailUrl ||
    item?.image ||
    item?.poster ||
    item?.backgroundUrl ||
    item?.thumb ||
    "";

  const titleText = item?.title ?? item?.name ?? "Untitled";

  // Media badge detection (accepts `type` or `expectedMediaType`)
  const mediaType = (item?.type || item?.expectedMediaType || "")
    .toString()
    .toLowerCase();

  const isVideo = mediaType === "video";
  const isImage = mediaType === "image";
  const isCollection = item?.type === "collection";

  const BadgeIcon = isVideo
    ? PlayArrowRoundedIcon
    : isCollection
    ? CollectionsBookmarkIcon
    : isImage
    ? ImageRoundedIcon
    : null;

  return (
    <Box
      ref={ref}
      role="button"
      data-focusable="true"
      data-row={rowIndex}
      data-col={index}
      tabIndex={-1}
      onClick={handleClick}
      onFocus={handleFocus}
      className="poster-root"
      sx={{
        width,
        outline: "none",
        borderRadius: `${radius}px`,
        overflow: "visible",
        position: "relative",

        // Make the ring visible when remote.js adds .focused, or on native :focus
        "&.focused .poster-focus-ring, &:focus .poster-focus-ring": {
          opacity: 1,
        },
      }}
      title={titleText}
      aria-label={titleText}
    >
      <Box
        className="poster-image"
        sx={{
          width,
          height,
          borderRadius: `${radius}px`,
          bgcolor: "grey.900",
          backgroundImage: bg ? `url(${bg})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Media type badge */}
        {BadgeIcon && (
          <Box
            sx={{
              position: "absolute",
              right: 8,
              bottom: 8,
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              zIndex: 2,            // above gradient/title, below focus ring border
              pointerEvents: "none" // do not steal focus/click
            }}
            aria-hidden
          >
            <BadgeIcon fontSize="small" />
          </Box>
        )}

        {overlayTitle && (
          <>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: `${Math.round(height * overlayHeightPct)}px`,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 85%, rgba(0,0,0,0.9) 100%)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                position: "absolute",
                left: 12,
                right: 12,
                bottom: 10,
                color: "#fff",
                fontWeight: 700,
                fontSize: `${overlayTitleSize}px`,
                lineHeight: 1.2,
                textShadow: "0 1px 2px rgba(0,0,0,0.7)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              {titleText}
            </Typography>
          </>
        )}

        {/* Focus ring above overlays */}
        <Box
          className="poster-focus-ring"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: `${radius}px`,
            border: "3px solid #fff",
            opacity: 0,                 // shown via parent selector above
            transition: "opacity 80ms ease",
            pointerEvents: "none",
            zIndex: 3,                  // ensure the ring sits on top
          }}
        />
      </Box>

      {!overlayTitle && (
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ mt: 0.5, width, color: "text.primary", fontSize: 18, fontWeight: 600 }}
        >
          {titleText}
        </Typography>
      )}
    </Box>
  );
}
